/** @jsxImportSource @emotion/react */
'use memo'

import { App } from 'antd'
import React, { useState } from 'react'
import WarningModal from 'src/components/WarningModal'
import type { FamilyMember } from '../hooks/useFamilyMembers'
import { useDeleteFamilyContact } from '../hooks/useDeleteFamilyContact'
import { useResendFamilyContact } from '../hooks/useResendFamilyContact'
import { FAMILY_SUCCESS_MESSAGES } from '@sportninja/common/features/profile/family/constants'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import InviteContactModal from './components/InviteContactModal'
import EditContactModal from './components/EditContactModal'

interface UseFamilyActionsResult {
  /** Open the Invite Family Member modal. */
  openInvite: () => void
  /** Open the Edit modal prefilled for a member (row ⋮ → Edit Information). */
  handleEdit: (member: FamilyMember) => void
  /** Open the Remove confirmation for a member (row ⋮ → Remove). */
  handleDelete: (member: FamilyMember) => void
  /** Resend a pending invitation for a member (row ⋮ → Resend). */
  handleResend: (member: FamilyMember) => void
  /** The Invite + Edit + Remove modals to mount once at the bottom of the consumer. */
  modals: React.ReactNode
}

/**
 * Shared family-contact actions (invite / edit / delete + the hosted modals).
 * Used by both the Family tab and the Overview tab's Family card so the row ⋮
 * behaves identically in both places.
 */
export const useFamilyActions = (
  playerId: string | null
): UseFamilyActionsResult => {
  const { message } = App.useApp()
  const deleteMutation = useDeleteFamilyContact()
  const resendMutation = useResendFamilyContact()

  const [inviteOpen, setInviteOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [memberBeingEdited, setMemberBeingEdited] =
    useState<FamilyMember | null>(null)
  const [memberBeingDeleted, setMemberBeingDeleted] =
    useState<FamilyMember | null>(null)

  const openInvite = () => setInviteOpen(true)

  const handleEdit = (member: FamilyMember) => {
    setMemberBeingEdited(member)
    setEditOpen(true)
  }

  const handleEditClose = () => {
    setEditOpen(false)
    setMemberBeingEdited(null)
  }

  const handleDelete = (member: FamilyMember) => {
    setMemberBeingDeleted(member)
  }

  const handleResend = async (member: FamilyMember) => {
    if (!playerId) {
      return
    }
    try {
      await resendMutation.mutateAsync({ playerId, contactId: member.id })
      message.success(formatSuccessToast(FAMILY_SUCCESS_MESSAGES.resend))
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Could not resend invitation'
      )
    }
  }

  const closeDelete = () => setMemberBeingDeleted(null)

  const confirmDelete = async () => {
    if (!playerId || !memberBeingDeleted) {
      return
    }
    try {
      await deleteMutation.mutateAsync({
        playerId,
        contactId: memberBeingDeleted.id,
      })
      // No success confirmation on delete — matches mobile (silent removal).
      closeDelete()
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Could not remove family member'
      )
    }
  }

  const modals = (
    <>
      <InviteContactModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        playerId={playerId}
      />
      <EditContactModal
        isOpen={editOpen}
        onClose={handleEditClose}
        playerId={playerId}
        member={memberBeingEdited}
      />
      <WarningModal
        isOpen={memberBeingDeleted != null}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title='Remove Family Member'
        description={
          <>
            Remove {memberBeingDeleted?.name ?? 'this contact'}?
            <br />
            They will lose access to this player.
          </>
        }
        cancelLabel='CANCEL'
        confirmLabel='REMOVE'
        loadingLabel='REMOVING…'
        isLoading={deleteMutation.isPending}
        destructive
      />
    </>
  )

  return { openInvite, handleEdit, handleDelete, handleResend, modals }
}
